import { useState, useEffect } from "react";
import Modal from "react-modal";

import { delayCall } from "api/util";

import { validatePhone, validatePostalCode } from "../../config/config";
import stateMappings from "../../config/states";
import Button from "../ui/Button";
import Dropdown from "../ui/Dropdown";

import { Checkbox, PhoneInputField, TextField } from "@/design-system";

Modal.setAppElement("#root");

const initialFormState = {
  fullName: "",
  address: "",
  address2: "",
  city: "",
  state: "ON",
  zip: "",
  phone: "",
  isResidential: false,
  country: "ca",
};

/**
 * The checkout's add-address form, in a modal for now — the reference draws the
 * same form inline in the Ship to card (grep `coShipFormOpen` in
 * `Dens N Dente Tailwind.dc.html`); whether to move it inline is an open
 * decision, so the reference's FIELD behaviour landed here where it survives
 * either answer:
 *
 * - DS `TextField` per field, validated on blur. TextField's own `onBlur` is
 *   taken by its focus ring, so validation rides React's bubbling blur on a
 *   wrapping div — the reference's exact pattern.
 * - Typing in a field clears its error; Save re-validates everything and bumps
 *   `errorNonce`, so still-invalid fields shake again on every attempt.
 * - "Set as my default address" (the reference's `coShipMakeDefault`) travels to
 *   `onSave` as a `makeDefault` flag on the form data — the caller strips it
 *   before the address reaches any payload.
 *
 * Country / state stay on the legacy native `Dropdown`, and the modal chrome
 * stays react-modal — only the fields are on the design system.
 */
export default function AddressModal({
  isOpen,
  onClose,
  onSave,
  addressType = "shipping",
}) {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [errorNonce, setErrorNonce] = useState(0);
  const [makeDefault, setMakeDefault] = useState(false);

  // Reset form whenever modal opens so second+ save always has a clean form
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormState);
      setErrors({});
      setMakeDefault(false);
    }
  }, [isOpen]);

  const fieldError = (name, data) => {
    if (name === "fullName") return data.fullName ? "" : "Enter the full name";
    if (name === "address")
      return data.address ? "" : "Enter the street address";
    if (name === "city") return data.city ? "" : "Enter the city";
    if (name === "state") return data.state ? "" : "Select a province";
    if (name === "zip") return validatePostalCode(data.zip, data.country) || "";
    if (name === "phone") {
      if (!data.phone) return "Enter a phone number";
      return validatePhone(data.phone)
        ? ""
        : "Enter a valid 10-digit phone number";
    }
    return "";
  };

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Typing is fixing — clear the field's error immediately, like the reference.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateField(name, type === "checkbox" ? checked : value);
  };

  // Blur bubbles from the input to this wrapper handler (TextField consumes the
  // input's own onBlur for its focus ring).
  const validateOnBlur = (name) => () =>
    setErrors((prev) => {
      const message = fieldError(name, formData);
      return message ? { ...prev, [name]: message } : prev;
    });

  const validateForm = () => {
    const newErrors = {};
    ["fullName", "address", "city", "state", "zip", "phone"].forEach((name) => {
      const message = fieldError(name, formData);
      if (message) newErrors[name] = message;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const isValid = validateForm();
    if (!isValid) {
      // One bump per attempt: every still-invalid field shakes again.
      setErrorNonce((nonce) => nonce + 1);
      return;
    }

    if (onSave) {
      onSave({ ...formData, makeDefault }, addressType);
      setFormData(initialFormState);
      setErrors({});
      setMakeDefault(false);
      // Defer close so parent state updates (new address) complete before closing
      delayCall(() => onClose(), 0);
    }
  };

  const states = stateMappings[formData.country?.toLowerCase?.()] || {};
  const stateOptions = Object.entries(states).map(([label, value]) => ({
    label: label
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
    value,
  }));

  const countryOptions = [{ label: "Canada", value: "ca" }];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Add Address"
      className="bg-white rounded-md p-6 w-full max-w-xl mx-auto mt-20 outline-none relative shadow-xl overflow-y-auto max-h-[90vh]"
      overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start z-50"
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          Add New {addressType === "shipping" ? "Shipping" : "Billing"} Address
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 text-xl hover:text-black"
        >
          ×
        </button>
      </div>

      <div className="flex flex-col gap-[var(--space-4)]">
        <div onBlur={validateOnBlur("fullName")}>
          <TextField
            label="Full name"
            name="fullName"
            required
            placeholder="First and last name"
            value={formData.fullName}
            onChange={handleChange}
            error={errors.fullName}
            errorNonce={errorNonce}
          />
        </div>
        <div onBlur={validateOnBlur("address")}>
          <TextField
            label="Street address"
            name="address"
            required
            placeholder="Street, unit"
            value={formData.address}
            onChange={handleChange}
            error={errors.address}
            errorNonce={errorNonce}
          />
        </div>
        <TextField
          name="address2"
          value={formData.address2}
          onChange={handleChange}
          placeholder="Apt. 3 or Suite #1516"
        />
        <div onBlur={validateOnBlur("city")}>
          <TextField
            label="City"
            name="city"
            required
            value={formData.city}
            onChange={handleChange}
            error={errors.city}
            errorNonce={errorNonce}
          />
        </div>
        <Dropdown
          label="Country *"
          name="country"
          value={formData.country}
          onChange={handleChange}
          options={countryOptions}
        />
        <Dropdown
          label="State/Province *"
          name="state"
          value={formData.state}
          onChange={handleChange}
          options={stateOptions}
          error={errors.state}
        />
        <div onBlur={validateOnBlur("zip")}>
          <TextField
            label="Postal code"
            name="zip"
            required
            placeholder="A1A 1A1"
            value={formData.zip}
            onChange={handleChange}
            error={errors.zip}
            errorNonce={errorNonce}
          />
        </div>
        <div onBlur={validateOnBlur("phone")}>
          <PhoneInputField
            name="phone"
            required
            value={formData.phone}
            onChange={(phone) => updateField("phone", phone)}
            error={errors.phone}
            errorNonce={errorNonce}
          />
        </div>
        <Checkbox
          shape="round"
          label="This is a residential address"
          checked={formData.isResidential}
          onChange={(checked) =>
            setFormData((prev) => ({ ...prev, isResidential: checked }))
          }
        />
        <Checkbox
          shape="round"
          label="Set as my default address"
          checked={makeDefault}
          onChange={(checked) => setMakeDefault(Boolean(checked))}
        />
      </div>

      <div className="mt-6 flex justify-end gap-4">
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save Address</Button>
      </div>
    </Modal>
  );
}
